import React, {
  useRef, useState, useEffect,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as IconArrowRight } from 'images/arrow-right.svg';
import { ReactComponent as IconCreateVideo } from 'images/icons/create-video.svg';
import { ReactComponent as IconVideoLoading } from 'images/icons/video-loading.svg';
import MessageModal from 'components/shared/modals/MessageModal';
import Button from 'components/shared/Button';
import { Textarea } from 'components/shared/Input';
import Field from 'components/shared/Field';
import TabInput from 'components/shared/TabInput';
import Switch from 'components/shared/Switch';
import useCategories from 'sdk/hooks/useCategories';
import useCreatePostWithOutUpload from 'sdk/hooks/useCreatePostWithOutUpload';
import { cloudApi, processFile } from 'sdk/api';
import AssetSelect from './AssetSelect';
import CategorySelect from './CategorySelect';
import PlaylistSelect from './PlaylistSelect';
import Question from './Question';
import styles from './EditForm.module.scss';
import CoverModal from '../CoverModal';
import UploadModal from './../UploadModal/index';
import { StringConstant } from '../../../../../utils/Constants';

const preselectedPlaylists = [
  { id: 20, name: 'About' },
  { id: 3, name: 'Pessimistic' },
  { id: 4, name: 'Favorable' },
];

const recommendations = [
  { id: 'sell', name: 'Sell' },
  { id: 'hold', name: 'Hold' },
  { id: 'buy', name: 'Buy' },
]

const risks = [
  { id: 'risky', name: 'Risky' },
  { id: 'neutral', name: 'Neutral' },
  { id: 'safe', name: 'Safe' },
]

function EditForm({ video, setActiveFile }: {
  video: any,
  setActiveFile: (v: any) => void,
}) {
  const inputRef = useRef(null)
  const [category, setCategory] = useState<any>()
  const [preselectedPlaylist,
    setPreselectedPlaylist] = useState<string | number | null | undefined>();
  const [playlist, setPlaylist] = useState<any>()
  const [assets, setAssets] = useState<any[]>([])
  const [description, setDescription] = useState('')
  const [hastags, setHastags] = useState([])
  const [isPro, setIsPro] = useState(false)
  const [holdAsset, setHoldAsset] = useState(false)
  const [isSponsored, setIsSponsored] = useState(false)
  const [showSuccess] = useState(false)
  const [recommendation, setRecommendation] = useState<string | number | null | undefined>(null)
  const [risk, setRisk] = useState<string | number | null | undefined>(null)
  const [isCoverModal, setIsCoverModal] = useState(false)
  const [isUploadModal, setIsUploadModal] = useState(false)
  const [isGeneral, setisGeneral] = useState(false)
  const [isBeginners, setIsBeginners] = useState(true)
  const [isInvesting] = useState(true)
  const [selectedCoverImage, setCoverSelectedImage] = useState('')
  const [coverImage, setCoverImage] = useState(null)
  const categoriesData = useCategories()

  const [videoData, setVideoData] = useState<any>();

  const [isValidVideo, setValidVideo] = useState(false)
  const [isValidAssets, setValidAssets] = useState(false)
  const [isValidCategories, setValidCategories] = useState(false)
  const [needUpload, setNeedUpload] = useState(false)
  const [isUploadVideo, setIsUploadVideo] = useState(false);
  const [progress, setProgress] = useState(0);

  const navigate = useNavigate()
  const isOtherCategory = category?.name === 'Other'
  const isBusinessandMoney = category?.name === StringConstant.ECONOMY// 'Business and Money'

  const handleSetCoverImage = async () => {
    const formData = new FormData()
    formData.append('file', selectedCoverImage)
    const response = await cloudApi.uploadMedia(formData)

    setCoverImage(response)
  }

  useEffect(() => {
    if (progress === 100) {
      setValidVideo(true)
    }
  })

  useEffect(() => {
    handleSetCoverImage()
  }, [selectedCoverImage])

  useEffect(() => {
    if (category) {
      setValidCategories(false)
    }
  }, [category])

  const createPostMutation = useCreatePostWithOutUpload(
    {
      description,
      category_id: category?.id || undefined,
      type: 'video',
      pro_subscription: isPro,
      recommendations: recommendation ? String(recommendation) : undefined,
      bg_cover: coverImage,
      is_beginners: isBeginners,
      is_about_investing: isInvesting,
      risk_level: risk,
      is_paid: isSponsored,
      hold_assets: holdAsset,
    },
    isOtherCategory ? [] : assets.map((v) => ({
      id: v.id,
      price: v.latest_price?.price || 0,
    })),
    [
      preselectedPlaylists.filter((item) => item.id === preselectedPlaylist)[0],
      ...(playlist ?? []),
    ],
    videoData?.secure_url || video?.url,
    hastags,
    videoData?.duration,
  )

  useEffect(() => {
    if (createPostMutation.isSuccess) {
      setTimeout(() => {
        // @ts-ignore
        navigate(`/post/${createPostMutation?.data?.id}`)
      }, 1000);
    }
  }, [createPostMutation.isSuccess])

  const selectFile = (event: any) => {
    const f = event.target.files[0]

    setValidVideo(false)

    setActiveFile({
      url: URL.createObjectURL(f),
      file: f,
    })
  }

  useEffect(() => {
    if (!assets?.length && !isOtherCategory && !isGeneral && !isBusinessandMoney) {
      setValidAssets(false)
    } else {
      setValidAssets(true)
    }
  }, [assets])

  useEffect(() => {
    if (!category) {
      setValidCategories(false)
    } else {
      setValidCategories(true)
    }
  }, [category])

  const handlePublish = () => {
    if (!videoData) {
      setNeedUpload(true)
      return
    }
    setNeedUpload(false)

    if (!isValidVideo || (!isValidAssets && isInvesting
      && !isOtherCategory) || !isValidCategories) {
      return
    }

    createPostMutation.mutate()
    setIsUploadModal(true)
  }

  const onClickUpload = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    // @ts-ignore
    return (inputRef?.current && inputRef?.current.click())
  }

  const onClickCover = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setIsCoverModal(true)
  }

  useEffect(() => {
    if (isUploadVideo) {
      setProgress(0)
    }
  }, [isUploadVideo])

  useEffect(() => {
    if (progress === 100) {
      setIsUploadVideo(false)
    }
  }, [progress])

  return (
    <div className={styles.container}>
      <div>

        <div
          className={styles.video}
          style={{ background: progress !== 100 ? 'rgba(141, 93, 218, 0.3)' : undefined }}
        >
          <form id="cover_video">
            <input
              ref={inputRef}
              className={styles.fileInput}
              type="file"
              name="file"
              id="fileee"
              disabled={isUploadVideo}
              accept="video/*"
              onChange={(e) => {
                selectFile(e)
                setIsUploadVideo(true)
                processFile(e, (value) => setProgress(value)).then((res) => setVideoData(res))
                  .then(() => setIsUploadVideo(false))
              }}
            />
          </form>
          <div className={styles.videoActions}>
            <Button disabled={isUploadVideo} onClick={onClickUpload}>Edit video</Button>
            <Button disabled={!video} onClick={onClickCover}>Cover</Button>
          </div>
          {
            progress !== 100 && ((
              <div className={styles.createVideo}>
                {
                  isUploadVideo ? (
                    <>
                      <IconVideoLoading />
                      <span>
                        {
                          progress !== 99 ? (
                            <>
                              Uploading:
                              {' '}
                              {progress}
                              %
                            </>
                          ) : (
                            <>
                              Processing video...
                            </>
                          )
                        }

                      </span>
                    </>

                  ) : (
                    <>
                      <IconCreateVideo />
                      <span>Add video</span>
                    </>
                  )
                }
              </div>
            ))
          }

          {
            progress === 100 && (
              <div className={styles.videoInner}>
                {selectedCoverImage ? <img className={styles.selectedCoverImage} src={selectedCoverImage} alt="" /> : <video id="videoRef" src={video?.url} />}
              </div>
            )
          }

        </div>
        {
          !isValidVideo && <div className={styles.textValidation}>*video required</div>
        }
      </div>

      <div className={styles.containerRight}>
        <h1 onClick={() => navigate(-1)}>
          <IconArrowRight />
          Publish
        </h1>
        <Field
          title="Description"
          info={(
            <span>
              Only up to
              {' '}
              <em>5 hashtags</em>
              {' '}
              allowed
            </span>
          )}
          infoRight={`${description.length}/2000 characters`}
          infoSmall
        >
          <Textarea
            value={description}
            onChange={(value: string) => {
              setDescription(value) // @ts-ignore
              setHastags(value.match(/#[^(\s,.;)]*/g) || [])
            }}
            rows={3}
            maxLength={2000}
            maxHashtag={5}
            width="507px"
            withHighlight
          />
        </Field>
        <Field
          title=""
        >
          <span className={styles.textBeginners}>
            Is this video intended to be for beginners?
          </span>
          <div className={styles.infoBeginners}>
            <Button secondary={!isBeginners} onClick={() => setIsBeginners(true)}>Yes</Button>
            <Button secondary={isBeginners} onClick={() => setIsBeginners(false)}>No</Button>
          </div>
        </Field>
        <Field
          title="Category"
          info={(
            <span>
              You can add some
              {' '}
              <em>additional information</em>
              {' '}
              on the post for better optimization
            </span>
          )}
          infoSmall
        >
          <CategorySelect
            className={styles.select}
            value={category}
            onChange={(value) => {
              setAssets([])
              setCategory(value)
            }}
            placeholder="Category"
            // isInvesting={isInvesting}
          />
          {
            !isValidCategories && <div className={styles.textValidation}>*category required</div>
          }
        </Field>
        {(isOtherCategory || (!isInvesting) || category?.name === StringConstant.ECONOMY
         || !category) ? null : (
           <Field title="Add assets">
             <Switch
               className={styles.swithInputGeneral}
               title="General (No specific asset)"
               value={isGeneral}
               onChange={() => {
                 setisGeneral((v: boolean) => !v)
                 setAssets([])
               }}
             />
             {
              !isGeneral && (
                <>
                  <AssetSelect
                    className={styles.select}
                    value={assets}
                    onChange={(e) => {
                      setAssets(e)
                      setValidAssets(false)
                      if (e.length === 1) {
                        setCategory(categoriesData.data?.find((c) => c.id === e[0].category_id))
                      }
                    }}
                    category={category}
                    disabled={isGeneral || !category}
                    placeholder="Add assets"
                  />
                  {!isValidAssets && <div className={styles.textValidation}>*assets required</div>}
                </>
              )
            }
           </Field>
          )}
        {category?.name !== 'Other' && isInvesting && !!assets.length && (
          <Field title="How would you tag this video">
            <TabInput
              values={preselectedPlaylists}
              value={preselectedPlaylist}
              onChange={setPreselectedPlaylist}
            />
          </Field>
        )}
        <Field
          title="Chose playlist"
          isOptional
          info={(
            <span>
              Depending on the playlist you choose, your post will be either shared on the
              {' '}
              <em>Community or Home screen</em>
            </span>
          )}
          infoSmall
        >
          <PlaylistSelect
            className={styles.select}
            value={playlist}
            onChange={setPlaylist}
            categoryId={category?.id || 0}
            placeholder="Chose playlist"
          />
        </Field>

        {(assets?.length) ? (

          <Question
            title={(assets?.length > 1) ? 'Do you hold these assets?' : 'Do you hold this asset?'}
            value={holdAsset}
            onChange={() => setHoldAsset((v: boolean) => !v)}
          />
        ) : null}
        <Question
          title={(assets?.length > 1) ? 'Are these paid partnerships?' : 'Is this paid partnership?'}
          value={isSponsored}
          onChange={() => setIsSponsored((v: boolean) => !v)}
        />

        {
          (isOtherCategory
            || category?.name === StringConstant.ECONOMY
            || !assets.length) ? null : (
              <>
                <Field
                  title="Recommendations"
                  isOptional
                >
                  <TabInput
                    values={recommendations}
                    value={recommendation}
                    onChange={setRecommendation}
                  />
                </Field>
                <Field
                  title="Risk Level"
                  isOptional
                >
                  <TabInput
                    values={risks}
                    value={risk}
                    onChange={setRisk}
                  />
                </Field>
              </>
            )
        }

        {false && (
          <Field title="Additional Settings">
            <Switch
              className={styles.swithInput}
              title="Make part of the pro subscription"
              value={isPro}
              onChange={() => setIsPro((v: boolean) => !v)}
              infoMessage="in order to generate users it is recommended to post most videos free to all users"
            />
          </Field>
        )}

        <Button
          className={styles.publishBtn}
          onClick={handlePublish}
          disabled={createPostMutation.isLoading
            || !isValidAssets || !isValidCategories || !isValidVideo}
        >
          {needUpload ? 'Publish' : 'Publish'}
        </Button>
      </div>

      {showSuccess ? (
        <MessageModal
          title="Success"
          status="success"
          onClick={() => navigate('/')}
          message="The post has been successfully published!"
        />
      ) : null}
      <CoverModal
        isOpen={isCoverModal}
        onClose={() => setIsCoverModal(false)}
        videoUrl={video.url}
        setCoverSelectedImage={setCoverSelectedImage}
      />
      {isUploadModal && (
        <UploadModal
          isSuccess={createPostMutation.isSuccess}
          onClick={() => setIsUploadModal(false)}
        />
      )}
    </div>
  )
}

export default EditForm
